import React from 'react';
import { useEventCallback } from '@cp/ds/src/hooks/useEventCallback';
import { useCurrentCity } from '@cp/entities/location';
import { CITY_PATH_PARAMETER, useClientWebRouteParams, usePathGenerator } from '@cp/entities/clientWebRoutes';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { useRouter } from '@cp/shared/router';
import { useClientCity } from './useClientCity';

enum RedirectionStage {
  NotNeeded = 'not needed',
  Watching = 'watching',
  Redirecting = 'redirecting',
  Complete = 'complete',
}

export const useRedirectToClientCity = () => {
  const router = useRouter();
  const { pageId, pageProps } = useClientWebRouteParams<ClientWebPage.Catalog>();
  const { currentCity } = useCurrentCity({ allowEmptyValue: true });
  const { clientCity, isLoading: isLoadingClientCity } = useClientCity();
  const generateAppPath = usePathGenerator();
  const redirectionStageRef = React.useRef<RedirectionStage>(
    clientCity || isLoadingClientCity ? RedirectionStage.NotNeeded : RedirectionStage.Watching,
  );
  const redirectToClientCity = useEventCallback(() => {
    if (pageId !== ClientWebPage.Catalog || !clientCity || clientCity.id === currentCity?.id) {
      return;
    }

    return router.navigate(
      generateAppPath({ page: ClientWebPage.Catalog, pageProps, pathParams: { [CITY_PATH_PARAMETER]: clientCity.alias } }),
    );
  });

  React.useEffect(() => {
    if (!clientCity && !isLoadingClientCity) {
      redirectionStageRef.current = RedirectionStage.Watching;
      return;
    }

    if (clientCity && redirectionStageRef.current === RedirectionStage.Watching) {
      redirectionStageRef.current = RedirectionStage.Redirecting;
      redirectToClientCity?.();
      redirectionStageRef.current = RedirectionStage.Complete;
      return;
    }
  }, [clientCity, isLoadingClientCity, pageId, redirectToClientCity]);
};
